<template>
  <div class="flex flex-col space-y-3">
    <div
      class="flex items-center justify-between"
      :class="{ 'border-b min-h-10': isDealView }"
    >
      <text-type-selector
        v-if="isDealView"
        :active-tab="activeTab"
        @click="handleActiveTab"
      />
      <div v-else class="flex">
        <lf-tab
          v-for="tab in tabs"
          :key="tab"
          :data-cy="`tab-${tab}`"
          :active="activeTab === tab"
          @click="handleActiveTab(tab)"
        >
          <lf-h4>{{ tab }}</lf-h4>
        </lf-tab>
      </div>
      <div v-if="activeText?.content" class="flex items-center space-x-1">
        <div
          v-if="subTabsKeys.length && !actionableState.isAdding && !isNoteData"
        >
          <lf-dropdown
            ref="providerSelector"
            :options="subTabs"
            :value="activeSubTab"
            :name="`subTab-${subTabs[activeSubTab]}`"
            :data-cy="`subTab-${subTabs[activeSubTab]}`"
            :teleport-options="{ enabled: isDealView }"
            custom-z-index="z-101"
            class="provider-selector"
          >
            <template #toggle-button="{ toggle, value, menuOpen }">
              <div
                ref="editableProviderContainer"
                :contenteditable="
                  actionableState.isEditingProvider && activeSubTab === value
                "
                class="flex items-center space-x-1 rounded bg-disabled p-1 px-2"
                :class="{
                  'cursor-pointer': !actionableState.isEditingProvider,
                }"
                @click.stop="
                  actionableState.isEditingProvider ? undefined : toggle()
                "
                @blur="handleEditingBlur($event, 'provider')"
              >
                <span>
                  {{ subTabs[value as number] }}
                </span>
                <icon-base
                  v-if="!actionableState.isEditingProvider"
                  height="6"
                  width="10"
                  :icon="IconDropdownArrow"
                  :class="{ 'rotate-180 transform': menuOpen }"
                />
              </div>
            </template>
            <template #listItem="{ index }">
              <div
                class="flex cursor-pointer items-center justify-between rounded p-2 hover:bg-[#EDF4FE]"
                :class="{ 'bg-gray-100': activeSubTab === index }"
                @click="handleActiveSubTab(index)"
              >
                <div class="text-headline">{{ subTabs[index] }}</div>
                <icon-base
                  v-if="activeSubTab === index"
                  :icon="IconCheck"
                  class="text-primary"
                />
              </div>
            </template>
          </lf-dropdown>
        </div>
        <template v-if="!isDealView">
          <icon-base
            v-if="showEditProviderButton"
            v-tooltip="`${$t('COMMON.EDIT')} ${$t('COMMON.PROVIDER')}`"
            class="cursor-pointer"
            :icon="IconEdit"
            @click.stop="handleEditingClick('provider', activeSubTab)"
          />
          <copy-to-clipboard-button
            v-if="activeText?.content"
            :value="activeText.content"
            class="p-0-5"
          />
          <button
            data-cy="search-toggle-button"
            class="mt-0-5 flex cursor-pointer items-center justify-center rounded p-1 hover:bg-gray-100"
            @click.prevent="toggleSearch"
          >
            <icon-base
              :icon="IconSearch"
              width="19"
              height="18"
              :fill="searchIconFill"
            />
          </button>
          <button
            v-if="!isNoteData"
            data-cy="delete-button"
            class="flex cursor-pointer items-center justify-center rounded p-1 text-disabled-medium hover:bg-red-100 hover:text-error"
            @click.prevent="actionableState.isDeleting = true"
          >
            <icon-base :icon="IconDelete" />
          </button>
        </template>
        <button
          v-if="
            subTabsKeys.length &&
            !actionableState.isAdding &&
            !searchBlock.isActive
          "
          v-tooltip="addTextMessage"
          data-cy="add-button"
          class="flex cursor-pointer items-center justify-center rounded p-1 text-disabled-medium hover:bg-gray-100"
          @click.prevent="handleAddText"
        >
          <icon-base
            :icon="IconAdd"
            width="20"
            height="20"
            :class="{ 'text-label': isDealView }"
          />
        </button>
        <action-menu-wrapper
          v-if="isDealView"
          class="mr-1"
          :items="menuItems"
          icon-type="horizontal"
          teleport
          :offset
          close-menu-on-trigger-hide
        />
      </div>
    </div>
    <div :class="isDealView ? 'min-h-5' : 'min-h-10'">
      <loader :is-loading="isLoading" />
      <div class="flex flex-col space-y-3">
        <div v-if="searchBlock.isActive" class="flex flex-col space-y-3">
          <search-input
            v-model="searchBlock.text"
            data-cy="call-text-search"
            class="h-10 w-full"
            no-padding
            no-margin
            @keyup="handleSearch"
          />
          <div
            v-if="searchBlock.results.length"
            class="flex items-center justify-between"
          >
            <lf-h4 data-cy="search-results-count">
              {{ searchCountTitle }}
            </lf-h4>
            <div class="flex items-center">
              <button
                v-tooltip="$t('DEALS.PREVIOUS_RESULT')"
                data-cy="search-previous-button"
                class="flex items-center justify-center rounded-sm p-1"
                :disabled="!searchBlock.currentResultIndex"
                :class="{
                  'hover:bg-gray-50': !!searchBlock.currentResultIndex,
                }"
                @click.stop.prevent="navigateToResult('prev')"
              >
                <icon-base
                  :icon="IconShrinkWhite"
                  height="21"
                  width="21"
                  icon-color="none"
                  class="rotate-90"
                />
              </button>
              <button
                v-tooltip="$t('DEALS.NEXT_RESULT')"
                data-cy="search-next-button"
                class="flex items-center justify-center rounded-sm p-1"
                :disabled="
                  searchBlock.currentResultIndex ===
                  searchBlock.results.length - 1
                "
                :class="{
                  'hover:bg-gray-50':
                    searchBlock.currentResultIndex <
                    searchBlock.results.length - 1,
                }"
                @click.stop.prevent="navigateToResult('next')"
              >
                <icon-base
                  :icon="IconShrinkWhite"
                  height="21"
                  width="21"
                  icon-color="none"
                  class="-rotate-90"
                />
              </button>
            </div>
          </div>
        </div>
        <div ref="textContainer" class="text-container overflow-y-auto">
          <highlight-text
            v-if="searchBlock.isActive"
            data-cy="highlighted-search-text"
            :needle="searchBlock.text"
            :content="handleNewLineForVHtml(activeText?.content || '')"
            :current-result-index="searchBlock.currentResultIndex"
            styles="search-item"
          />
          <form
            v-else-if="actionableState.isAdding || !hasTextData"
            class="flex flex-col space-y-2 pt-2"
            @focusout="addText"
          >
            <lf-input
              v-if="!isNoteData"
              name="provider"
              data-cy="new-text-provider-input"
              class="focus:ring-0"
              :placeholder="$t('COMMON.PROVIDER')"
              :value="newText.provider"
              @key-released="newText.provider = $event"
            />
            <lf-textarea
              ref="addTextBlock"
              v-model="newText.content"
              data-cy="new-text-content-textarea"
              class="focus:ring-0"
              :name="activeTypeName"
              rows="8"
              noresize
              :placeholder="addTextMessage"
            />
          </form>
          <content-show-hide
            v-else-if="activeText?.content && !actionableState.isEditing"
            class="content-show-hide cursor-text"
            data-cy="content-show-hide"
            :content="activeText.content"
            :initial-max-height="
              isDealView ? MAX_DEAL_HEIGHT : MAX_INBOX_HEIGHT
            "
            @text:click="handleEditingClick('text')"
          />
          <div
            v-else-if="activeText?.content && actionableState.isEditing"
            ref="editableTextContainer"
            :contenteditable="actionableState.isEditing"
            data-cy="call-text-editable"
            class="focus:outline-none"
            @blur="handleEditingBlur($event, 'text')"
            v-html="handleNewLineForVHtml(activeText?.content || '')"
          />
        </div>
      </div>
    </div>
    <confirm-modal
      v-if="actionableState.isDeleting"
      data-cy="delete-confirm-modal"
      :title="deleteModalText.title"
      :description="deleteModalText.description"
      :confirm-button="$t('COMMON.DELETE')"
      confirm-button-type="error"
      :close="() => (actionableState.isDeleting = false)"
      @confirm="deleteText"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import debounce from "lodash/debounce";
import escapeRegExp from "lodash/escapeRegExp";

import { isNullish } from "@/helpers/common";
import { UTIL_COLORS } from "@/helpers/constants";
import { handleNewLineForVHtml } from "@/helpers/formatting";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { getAsyncComponent, usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import { useUpdateCallNoteMutation } from "@/hooks/queries/useCommunication";
import communicationService from "@/services/modules/communication";
import { useCommunicationStore } from "@/stores/communication";

import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";
import HighlightText from "@/components/HighlightText.vue";
import ContentShowHide from "@/components/communicationLogs/communication/ContentShowHide.vue";
import TextTypeSelector from "@/components/deals/dealActivity/call/TextTypeSelector.vue";
import IconCheck from "@/components/icons/IconCheck.vue";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import IconShrinkWhite from "@/components/icons/IconShrinkWhite.vue";
import IconAdd from "@/components/icons/deals/IconAdd.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapper from "@/components/ui/ActionMenuWrapperV2.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfTab from "@/components/ui/LfTab.vue";
import Loader from "@/components/ui/Loader.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfTextarea from "@/components/ui/inputs/LfTextarea.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import LfH4 from "@/components/ui/text/LfHeading4.vue";

import type { CallLog } from "@/models/communicationLogs";

interface TextOptionType {
  content: string;
  provider: string | null;
  id: number;
}

const IconDelete = getAsyncComponent(
  () => import("@/components/icons/IconDelete.vue")
);

/**
 * For a deal view, the maximum height of the text container is 80px
 * (which is 2-3 lines of text).
 * For an inbox view, the maximum height of the text container is 600px
 * because there is more space available.
 */
const MAX_DEAL_HEIGHT = 80;
const MAX_INBOX_HEIGHT = 600;

const {
  callLog,
  callDisposition,
  isDealView = false,
} = defineProps<{
  callLog: CallLog;
  callDisposition: string;
  isDealView?: boolean;
}>();

const { t } = useI18n();

const { mutateAsync: updateCallNoteMutation } = useUpdateCallNoteMutation();
const { showMessage } = useNotification();
const copyToClipboard = useClipboardWithMessage();

const tabs = [
  t("COMMON.NOTE"),
  t("COMMON.SUMMARY"),
  t("COMMON.TRANSCRIPTION"),
] as const;

const emptyText: TextOptionType = {
  content: "",
  id: 0,
  provider: null,
};

const { updateLog } = useCommunicationStore();

const activeText = ref<TextOptionType>({
  id: 0,
  content: callLog?.data?.body ?? "",
  provider: null,
});
const activeTab = ref<string>(tabs[0]);
const activeSubTab = ref<number>(0);
const textOptions = ref<Record<(typeof tabs)[number], TextOptionType[]>>({
  [t("COMMON.NOTE")]: [],
  [t("COMMON.SUMMARY")]: [],
  [t("COMMON.TRANSCRIPTION")]: [],
});
const searchBlock = ref<{
  text: string;
  results: number[];
  currentResultIndex: number;
  isActive: boolean;
}>({
  text: "",
  results: [],
  currentResultIndex: 0,
  isActive: false,
});
const textContainer = useTemplateRef("textContainer");
const editableTextContainer = useTemplateRef("editableTextContainer");
const editableProviderContainer = useTemplateRef<HTMLDivElement | null>(
  "editableProviderContainer"
);
const providerSelector = useTemplateRef("providerSelector");
const addTextBlock = ref<InstanceType<typeof LfTextarea> | null>(null);
const actionableState = ref({
  isDeleting: false,
  isEditing: false,
  isAdding: false,
  isAddTextRequestLoading: false,
  isEditingProvider: false,
});

const newText = ref({
  provider: "",
  content: "",
});

const sessionId = computed(() => callLog?.data.id);

const showEditProviderButton = computed(
  () =>
    !isLoading.value &&
    !actionableState.value.isEditingProvider &&
    (activeText.value.provider || activeText.value.content) &&
    !isNoteData.value
);

const offset = computed(() =>
  isNoteData.value ? { left: -65, top: 5 } : { left: -100, top: 5 }
);

const menuItems = computed(() => {
  const items = [
    {
      label: t("COMMON.COPY"),
      handler: () => copyToClipboard(activeText.value.content),
    },
    {
      label: t("COMMON.SEARCH"),
      handler: toggleSearch,
    },
  ];

  if (
    !isLoading.value &&
    !actionableState.value.isEditingProvider &&
    (activeText.value.provider || activeText.value.content) &&
    !isNoteData.value
  ) {
    items.unshift({
      label: `${t("COMMON.EDIT")} ${t("COMMON.PROVIDER")}`,
      handler: () => handleEditingClick("provider", activeSubTab.value),
    });
  }

  if (!isNoteData.value) {
    items.push({
      label: t("COMMON.DELETE"),
      handler: () => {
        actionableState.value.isDeleting = true;
      },
    });
  }
  return items;
});

const isLoading = computed(() => {
  return (
    areSummariesLoading.value ||
    areTranscriptionsLoading.value ||
    isDeletingText.value ||
    actionableState.value.isAddTextRequestLoading
  );
});

const activeTypeName = computed(() => {
  if (isNoteData.value) {
    return t("COMMON.NOTE");
  }

  return isSummaryData.value ? t("COMMON.SUMMARY") : t("COMMON.TRANSCRIPTION");
});

const addTextMessage = computed(() => {
  return `${t("COMMON.ADD")} ${activeTypeName.value}`;
});

const subTabs = computed(() => {
  return getSubTabs(activeTab.value);
});

const subTabsKeys = computed(() => {
  return Object.keys(subTabs.value);
});

const isSummaryData = computed(() => {
  return activeTab.value === t("COMMON.SUMMARY");
});

const isNoteData = computed(() => {
  return activeTab.value === t("COMMON.NOTE");
});

const searchIconFill = computed(() => {
  return searchBlock.value.isActive ? UTIL_COLORS.PRIMARY : UTIL_COLORS.DEFAULT;
});

const searchCountTitle = computed(() => {
  const currentResult = searchBlock.value.currentResultIndex + 1;
  const resultsTotal = searchBlock.value.results.length;
  const text = t("COMMON.RESULT", resultsTotal === 1 ? 1 : 2);
  return `${currentResult}/${resultsTotal} ${text}`;
});

const deleteModalText = computed(() => {
  return {
    title: `${t("COMMON.DELETE")} ${activeTypeName.value}`,
    description: t("ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_DESCRIPTION", {
      name: activeTypeName.value,
    }),
  };
});

const hasTextData = computed(() => {
  if (isNoteData.value) {
    return !!callLog?.data.body;
  }

  return !!textOptions.value[activeTab.value].length;
});

const searchBlockHasResults = computed(
  () => !!searchBlock.value.results.length
);

const fetchRecordsAction = computed(() =>
  isSummaryData.value ? getSummaries : getTranscriptions
);

const getSubTabs = (tab: (typeof tabs)[number]) => {
  return textOptions.value[tab].reduce(
    (acc: Record<string, string>, { provider }, index) => {
      acc[index] = provider ?? `${tab} ${index + 1}`;
      return acc;
    },
    {}
  );
};

const handleActiveSubTab = (index: string | number) => {
  activeSubTab.value = Number(index);
  activeText.value = textOptions.value[activeTab.value][Number(index)];
  clearSearch();
  providerSelector.value?.toggleMenu();
};

const handleActiveTab = async (tab: (typeof tabs)[number], force = false) => {
  if (activeTab.value === tab && !force) {
    return;
  }

  activeTab.value = tab;

  if (isNoteData.value) {
    activeText.value = { ...emptyText, content: callLog?.data.body || "" };
  } else {
    activeText.value = emptyText;
    await fetchRecordsAction.value();
  }

  resetActionableState();
  clearSearch();
  searchBlock.value.isActive = false;
};

const resetActionableState = () => {
  actionableState.value.isAdding = !activeText.value?.content;
  actionableState.value.isEditing = false;
  actionableState.value.isDeleting = false;
  actionableState.value.isAddTextRequestLoading = false;
  actionableState.value.isEditingProvider = false;

  if (actionableState.value.isAdding) {
    nextTick(() => {
      addTextBlock.value?.textareaRef?.focus();
    });
  }
};

const getTextOptions = (data: TextOptionType[]) => {
  return data.reduce((acc: TextOptionType[], { content, id, provider }) => {
    if (content && id) {
      acc.push({ content, id, provider });
    }
    return acc;
  }, []);
};

const fetchData = async (tab: (typeof tabs)[number]) => {
  if (!sessionId.value) {
    return;
  }

  const request = isSummaryData.value
    ? communicationService.getCallLogSummaries
    : communicationService.getCallLogTranscriptions;

  const response = await request(sessionId.value.toString());

  textOptions.value[tab] = getTextOptions(response.data);

  activeText.value = textOptions.value[tab][0] ?? emptyText;
  activeSubTab.value = 0;
};

const findSearchResults = (text: string, searchTerm: string): number[] => {
  if (!searchTerm.trim()) {
    return [];
  }
  const regExp = new RegExp(escapeRegExp(searchTerm), "gi");
  const matches = Array.from(text.matchAll(regExp));
  return matches.reduce<number[]>((acc, match) => {
    if (!isNullish(match.index)) {
      acc.push(match.index);
    }
    return acc;
  }, []);
};

const updateSearchResults = () => {
  if (!searchBlock.value.text.trim()) {
    searchBlock.value.results = [];
    searchBlock.value.currentResultIndex = 0;
    return;
  }
  searchBlock.value.results = findSearchResults(
    activeText.value?.content || "",
    searchBlock.value.text
  );
  searchBlock.value.currentResultIndex = 0;
  if (searchBlockHasResults.value) {
    nextTick(() => {
      scrollToCurrentResult();
    });
  }
};

const scrollToCurrentResult = () => {
  if (!textContainer.value || !searchBlockHasResults.value) {
    return;
  }
  const searchElements = textContainer.value.querySelectorAll(".search-item");
  const currentElement = searchElements[searchBlock.value.currentResultIndex];
  if (currentElement) {
    searchElements.forEach((el) =>
      el.classList.remove("call-recording-text-active")
    );
    currentElement.classList.add("call-recording-text-active");

    const containerRect = textContainer.value.getBoundingClientRect();
    const elementRect = currentElement.getBoundingClientRect();
    const containerScrollTop = textContainer.value.scrollTop;
    const containerHeight = textContainer.value.clientHeight;

    const elementTop = elementRect.top - containerRect.top + containerScrollTop;
    const elementBottom = elementTop + elementRect.height;

    const isAboveView = elementTop < containerScrollTop;
    const isBelowView = elementBottom > containerScrollTop + containerHeight;

    if (isAboveView || isBelowView) {
      const scrollTo =
        elementTop - containerHeight / 2 + elementRect.height / 2;
      textContainer.value.scrollTo({
        top: Math.max(0, scrollTo),
        behavior: "smooth",
      });
    }
  }
};

const navigateToResult = (direction: "prev" | "next") => {
  if (direction === "prev") {
    if (!searchBlock.value.currentResultIndex) {
      return;
    }
    searchBlock.value.currentResultIndex--;
  } else {
    if (
      searchBlock.value.currentResultIndex ===
      searchBlock.value.results.length - 1
    ) {
      return;
    }
    searchBlock.value.currentResultIndex++;
  }
  nextTick(() => {
    scrollToCurrentResult();
  });
};

const toggleSearch = () => {
  searchBlock.value.isActive = !searchBlock.value.isActive;
  if (!searchBlock.value.isActive) {
    clearSearch();
  }
};

const clearSearch = () => {
  searchBlock.value.text = "";
  searchBlock.value.results = [];
  searchBlock.value.currentResultIndex = 0;
};

const handleNoteSaving = async (newText: string | null) => {
  if (!newText) {
    return;
  }

  await updateCallNoteMutation({
    id: callLog.data.id,
    payload: {
      note: newText,
      call_disposition: callDisposition,
    },
  });

  activeText.value = { ...emptyText, content: callLog?.data.body || "" };
};

const { fetchWrapper: handleEditingBlur } = usePromiseWrapper(
  async (event: FocusEvent, type: "text" | "provider") => {
    const target = event.target as HTMLDivElement | null;

    actionableState.value.isEditing = false;
    actionableState.value.isEditingProvider = false;

    const isText = type === "text";

    if (!target) {
      return;
    }

    const savedText = isText
      ? activeText.value?.content || ""
      : activeText.value?.provider || "";

    const newText = isText ? target.innerHTML : target.textContent;

    if (newText === savedText || !sessionId.value) {
      return;
    }

    actionableState.value.isAddTextRequestLoading = true;

    if (isNoteData.value) {
      await handleNoteSaving(newText);
      actionableState.value.isAddTextRequestLoading = false;
      return;
    }

    const action = isSummaryData.value
      ? communicationService.updateCallLogSummary
      : communicationService.updateCallLogTranscription;

    const payload = isText
      ? { content: newText ?? "" }
      : { provider: newText ?? "", content: activeText.value?.content || "" };

    if (isText && activeText.value?.provider) {
      payload.provider = activeText.value.provider;
    }

    const updatedText = await action(
      sessionId.value.toString(),
      activeText.value.id,
      payload
    );

    textOptions.value[activeTab.value] = textOptions.value[activeTab.value].map(
      (text) => (text.id === updatedText.id ? updatedText : text)
    );

    activeText.value = updatedText;
    actionableState.value.isAddTextRequestLoading = false;

    if (isText) {
      showMessage(t("NOTIFICATIONS.TEXT_UPDATED", { type: activeTab.value }));
    } else {
      showMessage(t("NOTIFICATIONS.PROVIDER_UPDATED"));
    }
  }
);

const handleEditingClick = (type: "text" | "provider", index?: number) => {
  if (type === "provider") {
    actionableState.value.isEditingProvider = true;
    nextTick(() => {
      if (index === undefined) {
        return;
      }
      editableProviderContainer.value?.focus();
    });
    return;
  }

  if (actionableState.value.isEditing) {
    return;
  }
  actionableState.value.isEditing = true;
  nextTick(() => {
    editableTextContainer.value?.focus();
  });
};

const handleAddText = () => {
  actionableState.value.isAdding = true;
  nextTick(() => {
    addTextBlock.value?.textareaRef?.focus();
  });
};

const getInitialActiveTab = async () => {
  if (callLog?.data.body) {
    return t("COMMON.NOTE");
  }

  if (callLog?.data.has_summary) {
    await handleActiveTab(t("COMMON.SUMMARY"));
    return t("COMMON.SUMMARY");
  }

  if (callLog?.data.has_transcription) {
    await handleActiveTab(t("COMMON.TRANSCRIPTION"));
    return t("COMMON.TRANSCRIPTION");
  }

  return t("COMMON.NOTE");
};

const handleSearch = debounce((event: KeyboardEvent) => {
  const { value } = event.target as HTMLInputElement;
  searchBlock.value.text = value.trim();
  updateSearchResults();
}, 600);

const { fetchWrapper: getSummaries, loading: areSummariesLoading } =
  usePromiseWrapper(async () => await fetchData(t("COMMON.SUMMARY")));

const { fetchWrapper: getTranscriptions, loading: areTranscriptionsLoading } =
  usePromiseWrapper(async () => await fetchData(t("COMMON.TRANSCRIPTION")));

const { fetchWrapper: addText } = usePromiseWrapper(
  async (event: FocusEvent) => {
    const currentTarget = event.currentTarget as HTMLFormElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    // If relatedTarget exists and is within the form, don't trigger the action
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    if (!sessionId.value || !newText.value.content.trim()) {
      newText.value = { content: "", provider: "" };
      actionableState.value.isAdding = false;
      return;
    }

    actionableState.value.isAddTextRequestLoading = true;

    if (isNoteData.value) {
      await handleNoteSaving(newText.value.content);
      Object.assign(actionableState.value, {
        isAdding: false,
        isAddTextRequestLoading: false,
      });
      newText.value = { content: "", provider: "" };
      return;
    }

    const action = isSummaryData.value
      ? communicationService.addCallLogSummary
      : communicationService.addCallLogTranscription;

    await action(sessionId.value.toString(), newText.value);
    await fetchRecordsAction.value();

    actionableState.value.isAdding = false;
    actionableState.value.isAddTextRequestLoading = false;

    updateLog({
      id: callLog.id,
      key: isSummaryData.value ? "has_summary" : "has_transcription",
      value: true,
    });

    activeSubTab.value = subTabsKeys.value.length - 1;
    activeText.value =
      textOptions.value[activeTab.value][
        textOptions.value[activeTab.value].length - 1
      ];
    newText.value = { content: "", provider: "" };
  }
);

const { fetchWrapper: deleteText, loading: isDeletingText } = usePromiseWrapper(
  async () => {
    if (!sessionId.value) {
      return;
    }

    const action = isSummaryData.value
      ? communicationService.deleteCallLogSummary
      : communicationService.deleteCallLogTranscription;

    await action(sessionId.value.toString(), activeText.value.id);
    await fetchRecordsAction.value();
    actionableState.value.isDeleting = false;
    if (!textOptions.value[activeTab.value].length) {
      updateLog({
        id: callLog.id,
        key: isSummaryData.value ? "has_summary" : "has_transcription",
        value: false,
      });
    }
  }
);

watch(
  () => searchBlock.value.currentResultIndex,
  () => {
    if (searchBlockHasResults.value) {
      nextTick(() => {
        scrollToCurrentResult();
      });
    }
  }
);

watch(
  () => activeText.value?.content,
  () => {
    if (searchBlock.value.isActive && searchBlock.value.text) {
      updateSearchResults();
    }
  }
);

watch(
  () => callLog,
  () => {
    handleActiveTab(activeTab.value, true);
  }
);

onMounted(async () => {
  activeTab.value = await getInitialActiveTab();
  if (!activeText.value?.content) {
    handleAddText();
  }
});
</script>
<style>
@reference "@/assets/tailwind.css";

.call-recording-text-active {
  @apply font-semibold text-headline;
}

.provider-selector .dropdown {
  @apply p-2;
}

.content-show-hide .text-container {
  @apply !border-none !p-0;
}

.content-show-hide .show-button {
  @apply !border-none;
}
</style>
